import { Flex, Grid, Paper, Tabs, Tooltip } from '@mantine/core';
import { IconExclamationCircle } from '@tabler/icons-react';
import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { getColorMantine } from '../../../../../../react-commons/components/inputs/text-fields/types';
import { type MantineColumn, MantineTable } from '../../../../../../react-commons/components/table/MantineTable';
import { ValidationEntrySeverity } from '../../../../../../react-commons/components/ValidationEntrySeverity';
import { ControlledInputs } from '../../../../../../react-commons/utils/read-only-inputs/controlledInputs';
import { formatIdentifier } from '../../../../../../react-commons/utils/utilFunctions';
import { ValidationEntryResultScopeType } from '../../../../resource/enumerations/ValidationEntryResultScopeType';
import {
    ValidationResource,
} from '../../../../resource/resources/validation/detailedValidationResource';
import { ScopeValidationItem, ValidationItem } from '../../../../resource/resources/validation/types';
import { ValidationEntryDetailsResource } from '../../../../resource/resources/validation/validationEntryDetailsResource';
import { useSystemInformationActions } from '../../../../resource/services/systemInformationService';
import { DependencyValidationResultComponent } from './DependencyValidationResultComponent';
import { ValidationScopeSectionComponent } from './ValidationScopeSectionComponent';

export const ValidationResourceResultTable = ({
                                                  validationItems,
                                                  persistableTypeGroup,
                                              }: {
    persistableTypeGroup: string;
    validationItems: Array<ValidationItem>;
}) => {
    const systemInformationActions = useSystemInformationActions();

    const validationMap = systemInformationActions.useGetValidationEntryDetails(persistableTypeGroup);

    const createDummyValidationEntryWithIdentifier = (identifier: string) => ({
        displayName: formatIdentifier(identifier),
        condition: '',
        description: null,
        defaultSeverity: ValidationEntrySeverity.NOTE,
        property: undefined,
        properties: undefined,
        persistableTypeFilter: null,
        targetProjectArchetypes: null,
    });

    const getValidationMapDetails = useCallback(
        (identifier: string): ValidationEntryDetailsResource => {
            const details =
                validationMap.data ?
                    (validationMap.data.property[identifier] ??
                        validationMap.data.child[identifier] ??
                        validationMap.data.compound[identifier] ??
                        createDummyValidationEntryWithIdentifier(identifier))
                    :   createDummyValidationEntryWithIdentifier(identifier);
            if (details.displayName === '') {
                return createDummyValidationEntryWithIdentifier(identifier);
            }
            return details;
        },
        [validationMap.data]
    );

    const columns = useMemo<Array<MantineColumn<ValidationItem>>>(() => {
        return [
            {
                type: 'string',
                header: 'Display Name',
                Header: null,
                accessorKey: 'identifier',
                Cell: ({ row }) => (
                    <ControlledInputs.String
                        nullable={false}
                        readOnly={true}
                        value={getValidationMapDetails(row.original.identifier).displayName}
                    />
                ),
            },
        ];
    }, [getValidationMapDetails]);

    return (
        <MantineTable
            columns={columns}
            data={validationItems}
            label={undefined}
            enableActions={true}
            rowActionsHeader={'Severity'}
            rowActionsWidth={55}
            tableOptions={{
                enableTopToolbar: false,
                enableBottomToolbar: false,
                enablePagination: false,
                renderRowActions: ({ row }) => (
                    <Flex justify="center" flex={1}>
                        <Tooltip label={row.original.severity}>
                            <IconExclamationCircle
                                style={{ marginLeft: 6 }}
                                color={getColorMantine(row.original.severity)}
                            />
                        </Tooltip>
                    </Flex>
                ),
                renderDetailPanel: ({ row }) => (
                    <SubContent
                        scope={row.original.scope}
                        description={getValidationMapDetails(row.original.identifier).description ?? undefined}
                        condition={getValidationMapDetails(row.original.identifier).condition}
                        detailedMessages={row.original.detailedMessages ?? undefined}
                    />
                ),
            }}
        />
    );
};

const SubContent: React.FunctionComponent<{
    scope: ScopeValidationItem | null | undefined;
    description?: string;
    condition?: string;
    detailedMessages?: Array<string>;
}> = ({ scope, description, condition, detailedMessages }) => {
    return (
        <Grid>
            {Boolean(scope && scope.type !== ValidationEntryResultScopeType.ANY_SCOPE) && (
                <Grid.Col span={12}>
                    <ValidationScopeSectionComponent scope={scope!} />
                </Grid.Col>
            )}
            {condition && (
                <Grid.Col span={description || detailedMessages?.length ? 6 : 12}>
                    <ControlledInputs.MultilineString
                        value={condition}
                        label={'Condition'}
                        maxRows={scope ? 3 : 5}
                        rows={scope ? 3 : 5}
                        autosize={false}
                        resize={false}
                        readOnly={true}
                        nullable={true}
                    />
                </Grid.Col>
            )}
            {(description || detailedMessages?.length) && (
                <Grid.Col span={condition ? 6 : 12}>
                    <ControlledInputs.MultilineString
                        value={
                            description && detailedMessages?.length ?
                                description.concat(...['\n', detailedMessages.join('\n')])
                                : detailedMessages?.length ?
                                    detailedMessages.join('\n')
                                    :   (description ?? '')
                        }
                        label={'Description'}
                        maxRows={scope ? 3 : 5}
                        rows={scope ? 3 : 5}
                        autosize={false}
                        resize={undefined}
                        readOnly={true}
                        nullable={true}
                    />
                </Grid.Col>
            )}
        </Grid>
    );
};

const InternalAllValidationResourceResultComponent = ({
                                                          persistableTypeGroup,
                                                          resourceValidation,
                                                      }: {
    persistableTypeGroup: string;
    resourceValidation: ValidationResource;
}) => {
    const allValidations = [
        ...(resourceValidation[ValidationEntrySeverity.FATAL]?.propertyValidation ?? []),
        ...(resourceValidation[ValidationEntrySeverity.CRITICAL]?.propertyValidation ?? []),
        ...(resourceValidation[ValidationEntrySeverity.WARNING]?.propertyValidation ?? []),
        ...(resourceValidation[ValidationEntrySeverity.NOTE]?.propertyValidation ?? []),
        ...(resourceValidation[ValidationEntrySeverity.FATAL]?.generalValidation ?? []),
        ...(resourceValidation[ValidationEntrySeverity.CRITICAL]?.generalValidation ?? []),
        ...(resourceValidation[ValidationEntrySeverity.WARNING]?.generalValidation ?? []),
        ...(resourceValidation[ValidationEntrySeverity.NOTE]?.generalValidation ?? []),
        ...(resourceValidation[ValidationEntrySeverity.FATAL]?.childValidation ?? []),
        ...(resourceValidation[ValidationEntrySeverity.CRITICAL]?.childValidation ?? []),
        ...(resourceValidation[ValidationEntrySeverity.WARNING]?.childValidation ?? []),
        ...(resourceValidation[ValidationEntrySeverity.NOTE]?.childValidation ?? []),
    ];

    return (
        <ValidationResourceResultTable
            persistableTypeGroup={persistableTypeGroup}
            validationItems={allValidations}
        />
    );
};

const InternalValidationResourceResultComponent = ({
                                                       persistableTypeGroup,
                                                       resourceValidation,
                                                       severity,
                                                   }: {
    persistableTypeGroup: string;
    severity: ValidationEntrySeverity;
    resourceValidation: ValidationResource;
}) => {
    return (
        <ValidationResourceResultTable
            persistableTypeGroup={persistableTypeGroup}
            validationItems={[
                ...(resourceValidation[severity]?.propertyValidation ?? []),
                ...(resourceValidation[severity]?.generalValidation ?? []),
                ...(resourceValidation[severity]?.childValidation ?? []),
            ]}
        />
    );
};

export const ValidationResourceResultComponent = ({
                                                      resourceValidation,
                                                      maxSeverity,
                                                      persistableTypeGroup,
                                                  }: {
    persistableTypeGroup: string;
    maxSeverity: ValidationEntrySeverity;
    resourceValidation?: ValidationResource | null;
}) => {
    const getSeverityAmount = (severity: ValidationEntrySeverity) => {
        if (!resourceValidation) {
            return 0;
        }

        const validationResource = resourceValidation[severity];
        return (
            (validationResource?.propertyValidation?.length ?? 0) +
            (validationResource?.generalValidation?.length ?? 0) +
            (validationResource?.childValidation?.length ?? 0)
        );
    };

    const shouldAllTabAppear = () => {
        const fatal = getSeverityAmount(ValidationEntrySeverity.FATAL);
        const critical = getSeverityAmount(ValidationEntrySeverity.CRITICAL);
        const warning = getSeverityAmount(ValidationEntrySeverity.WARNING);
        const note = getSeverityAmount(ValidationEntrySeverity.NOTE);

        const list = [fatal, critical, warning, note].filter((value) => value > 0);
        return Boolean(list.length > 1);
    };

    const getSingleSeverity = (): ValidationEntrySeverity | void => {
        if (shouldAllTabAppear()) {
            return;
        }

        const fatal = getSeverityAmount(ValidationEntrySeverity.FATAL);
        if (fatal > 0) {
            return ValidationEntrySeverity.FATAL;
        }

        const critical = getSeverityAmount(ValidationEntrySeverity.CRITICAL);
        if (critical > 0) {
            return ValidationEntrySeverity.CRITICAL;
        }

        const warning = getSeverityAmount(ValidationEntrySeverity.WARNING);
        if (warning > 0) {
            return ValidationEntrySeverity.WARNING;
        }

        const note = getSeverityAmount(ValidationEntrySeverity.NOTE);
        if (note > 0) {
            return ValidationEntrySeverity.NOTE;
        }
    };

    const [validationSeverityTab, setValidationSeverityTab] = React.useState<string | null>(
        shouldAllTabAppear() ? 'all' : maxSeverity
    );

    if (!resourceValidation) {
        return <div></div>;
    }

    const singleSeverity = getSingleSeverity();
    if (singleSeverity) {
        return (
            <InternalValidationResourceResultComponent
                resourceValidation={resourceValidation}
                severity={singleSeverity}
                persistableTypeGroup={persistableTypeGroup}
            />
        );
    }

    return (
        <Tabs value={validationSeverityTab} onChange={setValidationSeverityTab}>
            <Tabs.List>
                {shouldAllTabAppear() && (
                    <Tabs.Tab value={'all'} style={{ height: 28, minHeight: 28 }}>
                        All Severities
                    </Tabs.Tab>
                )}
                {Boolean(getSeverityAmount(ValidationEntrySeverity.FATAL)) && (
                    <Tabs.Tab
                        value={ValidationEntrySeverity.FATAL}
                        style={{ height: 28, minHeight: 28 }}
                        rightSection={<IconExclamationCircle color={getColorMantine(ValidationEntrySeverity.FATAL)} />}
                    >
                        Fatal
                    </Tabs.Tab>
                )}
                {Boolean(getSeverityAmount(ValidationEntrySeverity.CRITICAL)) && (
                    <Tabs.Tab
                        value={ValidationEntrySeverity.CRITICAL}
                        style={{ height: 28, minHeight: 28 }}
                        rightSection={
                            <IconExclamationCircle color={getColorMantine(ValidationEntrySeverity.CRITICAL)} />
                        }
                    >
                        Critical
                    </Tabs.Tab>
                )}
                {Boolean(getSeverityAmount(ValidationEntrySeverity.WARNING)) && (
                    <Tabs.Tab
                        value={ValidationEntrySeverity.WARNING}
                        style={{ height: 28, minHeight: 28 }}
                        rightSection={
                            <IconExclamationCircle color={getColorMantine(ValidationEntrySeverity.WARNING)} />
                        }
                    >
                        Warning
                    </Tabs.Tab>
                )}
                {Boolean(getSeverityAmount(ValidationEntrySeverity.NOTE)) && (
                    <Tabs.Tab
                        value={ValidationEntrySeverity.NOTE}
                        style={{ height: 28, minHeight: 28 }}
                        rightSection={<IconExclamationCircle color={getColorMantine(ValidationEntrySeverity.NOTE)} />}
                    >
                        Note
                    </Tabs.Tab>
                )}
            </Tabs.List>
            <Tabs.Panel value={'all'}>
                <InternalAllValidationResourceResultComponent
                    resourceValidation={resourceValidation}
                    persistableTypeGroup={persistableTypeGroup}
                />
            </Tabs.Panel>
            <Tabs.Panel value={ValidationEntrySeverity.FATAL}>
                <InternalValidationResourceResultComponent
                    resourceValidation={resourceValidation}
                    severity={ValidationEntrySeverity.FATAL}
                    persistableTypeGroup={persistableTypeGroup}
                />
            </Tabs.Panel>
            <Tabs.Panel value={ValidationEntrySeverity.CRITICAL}>
                <InternalValidationResourceResultComponent
                    resourceValidation={resourceValidation}
                    severity={ValidationEntrySeverity.CRITICAL}
                    persistableTypeGroup={persistableTypeGroup}
                />
            </Tabs.Panel>
            <Tabs.Panel value={ValidationEntrySeverity.WARNING}>
                <InternalValidationResourceResultComponent
                    resourceValidation={resourceValidation}
                    severity={ValidationEntrySeverity.WARNING}
                    persistableTypeGroup={persistableTypeGroup}
                />
            </Tabs.Panel>
            <Tabs.Panel value={ValidationEntrySeverity.NOTE}>
                <InternalValidationResourceResultComponent
                    resourceValidation={resourceValidation}
                    severity={ValidationEntrySeverity.NOTE}
                    persistableTypeGroup={persistableTypeGroup}
                />
            </Tabs.Panel>
        </Tabs>
    );
};
